import { motion } from 'framer-motion';
import { initialGoUp, initialGoDown, whileInView, transition } from '../../../helpers/animations';
import { useTranslation } from 'react-i18next';
import { getPrizesTranslations, useHomeTranslationsQuery } from '../../../services/HomePageServices';
import parse from 'html-react-parser';
import { Container } from 'react-bootstrap';
import { sanitizeCmsHtml } from '../../../helpers/sanitizeCmsHtml';

interface PrizeCardProps {
  idx: number;
}

interface PrizesSectionProps {
  inline?: boolean;
}

function PrizeCard({ idx }: PrizeCardProps) {
  const { t } = useTranslation('prizes_and_awards');

  return (
    <article className='prizes-and-awards__card'>
      <div className='prizes-and-awards__icon-column'>
        <img
          alt={t(`top_winners_card_icon_alt_text_${idx}`)}
          src={t(`top_winners_card_icon_link_${idx}`)}
          className='prizes-and-awards__icon'
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className='prizes-and-awards__content-column'>
        <h4 className='prizes-and-awards__card-title'>
          {t(`top_winners_card_title_${idx}`)}
        </h4>
        <div className='prizes-and-awards__card-description'>
          {parse(sanitizeCmsHtml(t(`top_winners_card_description_${idx}`)))}
        </div>
      </div>
    </article>
  );
}

function PrizesSection({ inline = false }: PrizesSectionProps) {
  const { t } = useTranslation(['prizes_and_awards', 'general_site']);

  const homeQuery = useHomeTranslationsQuery();
  const isLoading = homeQuery.isLoading;
  const isError = homeQuery.isError;
  const prizeCardIndexes = homeQuery.data ? getPrizesTranslations(homeQuery.data) : undefined;

  if (isLoading) return null;

  const Wrapper = inline ? 'div' : 'section';

  if (isError || !prizeCardIndexes) return null;

  return (
    <Wrapper id={inline ? undefined : 'prizes-and-awards'} className="prizes-and-awards">
      <Container className='prizes-and-awards__container'>
        <motion.div
          initial={initialGoDown}
          whileInView={whileInView}
          viewport={{
            once: true,
            amount: 0.2
          }}
          transition={transition}
        >
          <p className="prizes-and-awards__section-name">{t('prizes_section_name')}</p>
        </motion.div>

        <motion.div
          initial={initialGoUp}
          whileInView={whileInView}
          viewport={{
            once: true,
            amount: 0.2
          }}
          transition={transition}
        >
          <h2 className="prizes-and-awards__title">{t('top_winners_title')}</h2>

          <div className="prizes-and-awards__cards-grid row" role="list" aria-label={t('top_winners_title')}>
            {
              prizeCardIndexes.top_winners_card_idx.map((idx) => (
                <div key={`top_winner_${idx}`} className="col col-xl-4" role="listitem">
                  <PrizeCard idx={idx}/>
                </div>
              ))
            }
          </div>
        </motion.div>
      </Container>
    </Wrapper>
  );
}

export default PrizesSection;
